import clsx from "clsx";
import React from "react";
import { drawer, drawerOpen, drawerSide, overlay } from "./Drawer.css";

export interface DrawerProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Whether the drawer is open (slid into view). When closed it stays mounted
   * off-screen so its content and scroll position persist.
   */
  open: boolean;
  /**
   * Called when the user dismisses the drawer by clicking the backdrop.
   */
  onClose: () => void;
  /**
   * Width of the panel, as any CSS length. Defaults to `420px`.
   */
  width?: string;
  /**
   * Accessible label for the dismiss backdrop. Defaults to `"Close"`.
   */
  closeLabel?: string;
  /**
   * Which edge the panel is anchored to, and the edge it slides in from.
   * Defaults to `"right"`.
   */
  position?: keyof typeof drawerSide;
}

/**
 * A right-anchored overlay panel that slides in from the edge over a dismiss
 * backdrop. It's a presentational shell only — the caller provides the header,
 * body, and footer as children and owns the `open` state.
 */
export function Drawer({
  open,
  onClose,
  width = "420px",
  closeLabel = "Close",
  position = "right",
  className,
  children,
  style,
  ...props
}: DrawerProps) {
  return (
    <>
      {open && (
        <button
          type="button"
          aria-label={closeLabel}
          className={overlay}
          onClick={onClose}
        />
      )}
      <aside
        aria-hidden={!open}
        className={clsx(
          drawer,
          drawerSide[position],
          open && drawerOpen,
          className
        )}
        style={{ width, ...style }}
        {...props}
      >
        {children}
      </aside>
    </>
  );
}
